/**
 * PolicyEditorSections — the Limits, Permissions and Provider tabs of the policy form.
 * IdentitySection and RoutingSection live in PolicyEditorSectionsA.tsx.
 * Template example: NextAdmin `profile/account/page.tsx` + demo `/form-layouts` (FormSection two-column grid).
 */
import { Shield } from 'lucide-react';
import type React from 'react';
import { useWatch } from 'react-hook-form';
import {
  FormFullWidth,
  FormSection,
  JsonFormField,
  NumberFormField,
  SelectFormField,
  SwitchFormField,
} from '../../../components/form/index.js';
import { Callout } from '../../../components/ui/index.js';
import { TagsFormField } from './FieldPrimitives.js';
import type { SectionProps } from './PolicyEditorSectionsA.js';
import { RUNTIME_POLICY_FIELD_HELP } from '../../../help/runtime-policy-fields.js';
import { PERMISSION_MODES, SESSION_MODES, enumOptions } from './types.js';

/** A spend cap above this needs a second approver (advisory: the server does not enforce it yet). */
const HIGH_SPEND_USD = 25;

export const LimitsSection: React.FC<SectionProps> = ({ canEdit }) => (
  <>
    <FormSection title="Limits" description="How much one run may use before it is stopped.">
      <NumberFormField
        name="maxTurns"
        help={RUNTIME_POLICY_FIELD_HELP.maxTurns}
        label="Max turns"
        disabled={!canEdit}
        minValue={1}
      />
      <NumberFormField
        name="maxToolCalls"
        help={RUNTIME_POLICY_FIELD_HELP.maxToolCalls}
        label="Max tool calls"
        disabled={!canEdit}
        minValue={0}
        description="0 allows any number of tool calls."
      />
      <NumberFormField
        name="maxInputTokens"
        help={RUNTIME_POLICY_FIELD_HELP.maxInputTokens}
        label="Max input tokens"
        disabled={!canEdit}
        emptyAs={null}
        placeholder="Unlimited"
      />
      <NumberFormField
        name="maxOutputTokens"
        help={RUNTIME_POLICY_FIELD_HELP.maxOutputTokens}
        label="Max output tokens"
        disabled={!canEdit}
      />
      <NumberFormField
        name="maxConcurrentRequests"
        help={RUNTIME_POLICY_FIELD_HELP.maxConcurrentRequests}
        label="Max calls at once per model"
        disabled={!canEdit}
        emptyAs={null}
        minValue={1}
        placeholder="The model's limit"
      />
      <NumberFormField
        name="wallClockTimeoutMs"
        help={RUNTIME_POLICY_FIELD_HELP.wallClockTimeoutMs}
        label="Wall-clock timeout (ms)"
        disabled={!canEdit}
        description="The whole run, including retries."
      />
      <NumberFormField
        name="idleTimeoutMs"
        help={RUNTIME_POLICY_FIELD_HELP.idleTimeoutMs}
        label="Idle timeout (ms)"
        disabled={!canEdit}
        emptyAs={null}
        placeholder="None"
      />
    </FormSection>
    <FormSection title="Retry and timeouts" description="What happens when the provider fails or is slow.">
      <NumberFormField
        name="retryMax"
        help={RUNTIME_POLICY_FIELD_HELP.retryMax}
        label="Retry max"
        disabled={!canEdit}
        minValue={0}
      />
      <NumberFormField
        name="retryBackoffMs"
        help={RUNTIME_POLICY_FIELD_HELP.retryBackoffMs}
        label="Backoff (ms)"
        disabled={!canEdit}
        minValue={0}
      />
      <NumberFormField
        name="retryBackoffMultiplier"
        help={RUNTIME_POLICY_FIELD_HELP.retryBackoffMultiplier}
        label="Backoff multiplier"
        disabled={!canEdit}
        formatOptions={{ maximumFractionDigits: 2 }}
      />
      <NumberFormField
        name="providerRequestTimeoutMs"
        help={RUNTIME_POLICY_FIELD_HELP.providerRequestTimeoutMs}
        label="Provider request timeout (ms)"
        disabled={!canEdit}
      />
      <NumberFormField
        name="spendCapUsd"
        help={RUNTIME_POLICY_FIELD_HELP.spendCapUsd}
        label="Spend cap (USD)"
        disabled={!canEdit}
        emptyAs={null}
        placeholder="None"
        minValue={0}
        formatOptions={{ maximumFractionDigits: 2 }}
      />
      <SwitchFormField
        name="retryJitter"
        help={RUNTIME_POLICY_FIELD_HELP.retryJitter}
        label="Jitter"
        description="Randomise the wait between retries."
        disabled={!canEdit}
      />
    </FormSection>
  </>
);

/** The advisory shown while a setting raises this policy's permission ceiling, or null when none does. */
function guardrailMessage(permissionMode: string | undefined, riskTier: string | undefined, spendCapUsd: unknown) {
  if (permissionMode === 'bypass_permissions') {
    return 'Permission bypass is on: this policy can use any provider tool without restriction. Bypass needs a second approver before it becomes active (advisory: the server does not enforce this yet).';
  }
  if (riskTier === 'privileged') {
    return 'Privileged policy: only a superadmin can use it, and the resolver never picks it automatically. It can only be chosen by name.';
  }
  if (typeof spendCapUsd === 'number' && spendCapUsd > HIGH_SPEND_USD) {
    return `Spend cap above $${HIGH_SPEND_USD}: raising it needs a second approver (advisory: the server does not enforce this yet).`;
  }
  return null;
}

export const PermissionsSection: React.FC<SectionProps> = ({ canEdit }) => {
  const [permissionMode, riskTier, spendCapUsd] = useWatch({ name: ['permissionMode', 'riskTier', 'spendCapUsd'] });
  const message = guardrailMessage(permissionMode, riskTier, spendCapUsd);

  return (
    <>
      {message && (
        <Callout tone="danger" icon={Shield} title="Guardrail">
          {message}
        </Callout>
      )}
      <FormSection title="Permissions" description="What the model may do while a run uses this policy.">
        <SelectFormField
          name="permissionMode"
          help={RUNTIME_POLICY_FIELD_HELP.permissionMode}
          label="Permission mode"
          disabled={!canEdit}
          options={enumOptions(PERMISSION_MODES)}
        />
        <SelectFormField
          name="sessionMode"
          help={RUNTIME_POLICY_FIELD_HELP.sessionMode}
          label="Session mode"
          disabled={!canEdit}
          options={enumOptions(SESSION_MODES)}
        />
        <FormFullWidth>
          <TagsFormField
            name="allowedProviderTools"
            help={RUNTIME_POLICY_FIELD_HELP.allowedProviderTools}
            label="Allowed provider tools"
            tone="teal"
            disabled={!canEdit}
            emptyText="All provider tools are allowed"
            placeholder="Add a tool and press Enter"
            description="Leave empty to allow every provider tool."
          />
        </FormFullWidth>
      </FormSection>
    </>
  );
};

export const ProviderSection: React.FC<SectionProps> = ({ canEdit }) => {
  const extensions = useWatch({ name: 'providerExtensions' });
  const count = extensions && typeof extensions === 'object' ? Object.keys(extensions).length : 0;

  return (
    <FormSection title="Provider behavior" description="Settings passed to the provider for every run.">
      <SwitchFormField
        name="streamingEnabled"
        help={RUNTIME_POLICY_FIELD_HELP.streamingEnabled}
        label="Streaming"
        disabled={!canEdit}
      />
      <SwitchFormField
        name="promptCachingEnabled"
        help={RUNTIME_POLICY_FIELD_HELP.promptCachingEnabled}
        label="Prompt caching"
        disabled={!canEdit}
      />
      <NumberFormField
        name="maxSystemPromptChars"
        help={RUNTIME_POLICY_FIELD_HELP.maxSystemPromptChars}
        label="Max system prompt characters"
        disabled={!canEdit}
        emptyAs={null}
        placeholder="Unlimited"
      />
      <FormFullWidth>
        <JsonFormField
          name="providerExtensions"
          help={RUNTIME_POLICY_FIELD_HELP.providerExtensions}
          label="Provider extensions (JSON)"
          disabled={!canEdit}
          rows={8}
          description={`${count} ${count === 1 ? 'key' : 'keys'}. Provider-specific options, keyed by provider.`}
        />
      </FormFullWidth>
    </FormSection>
  );
};
